import React, { useState } from "react";
import "../css/WelcomeTutorial.css";
import { useAuthContext } from "../hooks/useAuthContext";

const WelcomeTutorial = ({ onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { user } = useAuthContext();

  const slides = [
    {
      title: "Welcome to CS UF!",
      description: `Welcome ${user?.username}! Get ready to dive into the world of competitive Counter-Strike fantasy teams.`,
      icon: "Trophy1",
      points: [
        "Build fantasy teams with player cards",
        "Compete in real CS2 events",
        "Climb the seasonal leaderboards and win rewards",
      ],
    },
    {
      title: "Getting Started",
      description:
        "Your journey begins with opening packs and building your inventory.",
      icon: "OpenPackIcon3",
      points: [
        "Claim your free daily pack every once a day",
        "Open packs to get player cards",
        "Each card has unique boosts",
        "You start with 5000 coins to get going!",
      ],
    },
    {
      title: "Building Your Team",
      description: "Create fantasy teams for upcoming CS2 events.",
      icon: "Cards3",
      points: [
        "Select 5 unique player cards for each event",
        "Maximum 2 players from the same team",
        "Modify your team before the event starts",
        "Choose cards with boosts matching player strengths",
      ],
    },
    {
      title: "Earning Points",
      description: "Points are based on real player performance in CS2 events.",
      icon: "Points3",
      points: [
        "Players earn points from their HLTV rating",
        "Card boosts gain additional points for specific stats (kills, assists, etc.)",
        "Team wins give ±100 points per map win/loss",
        "Compete on season and event leaderboards",
      ],
    },
    {
      title: "Extra Features",
      description: "Explore more ways to enhance your experience.",
      icon: "TBC3",
      points: [
        "Complete Team Building Challenges for rewards",
        "Use Clutch Picks to earn cards from other players",
        "Trade cards on the Market",
        "Join or create Leagues to compete against friends and set custom rewards",
        "Customize your profile with unlockable cosmetics",
      ],
    },
    {
      title: "You're All Set!",
      description: "Ready to start your journey to the top of the leaderboard?",
      icon: "Trophy2",
      points: [
        "Claim your free daily pack now in the Packs section",
        "Test your luck with Clutch Picks",
        "Check upcoming events and build your first team",
        'Visit "How to Play" anytime for detailed guides',
        "Good luck and have fun!",
      ],
    },
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleFinish = async () => {
    // Mark tutorial as seen in backend
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/users/tutorial-seen`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        onClose();
      }
    } catch (error) {
      console.error("Error marking tutorial as seen:", error);
      // Close anyway
      onClose();
    }
  };

  const slide = slides[currentSlide];

  return (
    <div className="tutorial-overlay">
      <div className="tutorial-modal">
        <div className="tutorial-content">
          <div className="tutorial-header">
            <h1 className="tutorial-title">{slide.title}</h1>
            <p className="tutorial-description">{slide.description}</p>
          </div>

          {slide.icon && (
            <div className="tutorial-image-container">
              <div className={`tutorial-icon-display ${slide.icon}`}></div>
            </div>
          )}

          <div className="tutorial-points" key={currentSlide}>
            <ul>
              {slide.points.map((point, index) => (
                <li key={index} className="tutorial-point">
                  <span className="point-bullet">✓</span>
                  <span className="point-text">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="tutorial-progress">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${
                  index === currentSlide ? "active" : ""
                } ${index < currentSlide ? "completed" : ""}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>

          <div className="tutorial-navigation">
            <button
              className="tutorial-btn tutorial-btn-secondary"
              onClick={handlePrevious}
              disabled={currentSlide === 0}
            >
              Previous
            </button>

            <span className="slide-counter">
              {currentSlide + 1}
              <span style={{ fontFamily: "fantasy" }}> / </span>
              {slides.length}
            </span>

            <button
              className="tutorial-btn tutorial-btn-primary"
              onClick={handleNext}
            >
              {currentSlide === slides.length - 1 ? "Get Started!" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTutorial;
